import { useQuery } from '@tanstack/react-query';

import type { GetSubmitSignedTarPaperworkResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get submit signed TAR paperwork status from a purchased vehicle
 */
export const getSubmitSignedTarPaperwork = async (
	servicesProviderId: string,
	orderId: string,
): Promise<GetSubmitSignedTarPaperworkResponse> => {
	const response = await apiClient.get<GetSubmitSignedTarPaperworkResponse>(
		ordersEndpoints.submitSignedTarPaperwork(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for getting submit signed TAR paperwork status from a purchased vehicle
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetSubmitSignedTarPaperwork = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.progress._ctx
			.submitSignedTarPaperwork.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error(
					'Unable to get submit signed TAR paperwork status without an order ID.',
				);
			}
			return getSubmitSignedTarPaperwork(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
